import { useState } from "react";

import { CanvasBlockRenderer } from "./components/canvas/CanvasBlockRenderer";
import { type CanvasContent } from "./components/canvas/types";
import { CanvasPane } from "./components/CanvasPane";
import { ProjectLoadingPane } from "./components/ProjectLoadingPane";

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

function StyleGuideSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-puntt-neutral-gray-9">
        {title}
      </h2>
      <div className="rounded-xl border border-puntt-neutral-gray-5 bg-base-white p-5">
        {children}
      </div>
    </section>
  );
}

function BlockRow({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap gap-3">{children}</div>;
}

function BlockPreview({ children }: { children: React.ReactNode }) {
  return <div className="min-w-0 flex-1">{children}</div>;
}

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

const FULL_CANVAS_DEMO: CanvasContent = {
  outputType: "risk_assessment",
  exportFormats: ["pdf", "csv"],
  blocks: [
    { type: "heading", level: 1, text: "Q3 Brand Risk Assessment" },
    {
      type: "callout",
      variant: "warning",
      text: "Three high-priority issues require immediate attention before the campaign launch.",
    },
    {
      type: "section",
      id: "s1",
      title: "Executive Summary",
      children: [
        {
          type: "paragraph",
          text: "This assessment covers creative compliance, brand consistency, and regulatory exposure across 47 assets reviewed this quarter.",
        },
      ],
    },
    {
      type: "grid",
      columns: 3,
      children: [
        { type: "metric", label: "Assets Reviewed", value: "47", trend: "up" },
        { type: "metric", label: "High Risk", value: "3", trend: "down" },
        {
          type: "metric",
          label: "Brand Score",
          value: "82%",
          trend: "neutral",
        },
      ],
    },
    {
      type: "section",
      id: "s2",
      title: "Issues Found",
      children: [
        {
          type: "table",
          headers: ["Asset", "Issue", "Severity"],
          rows: [
            ["Hero Banner", "Off-brand color usage", "High"],
            ["Email Header", "Missing legal copy", "Medium"],
            ["Social Post", "Unapproved font", "Low"],
          ],
          highlightRules: { High: "red", Medium: "amber", Low: "gray" },
        },
      ],
    },
    {
      type: "source_link",
      title: "Brand Guidelines v2.4",
      url: "#",
      linkType: "document",
    },
  ],
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AskPunttStyleGuide() {
  const [showLoadingPane, setShowLoadingPane] = useState(false);
  const [canvasDismissed, setCanvasDismissed] = useState(false);

  return (
    <div className="h-full overflow-y-auto bg-puntt-neutral-gray-2 px-8 py-10">
      <div className="mx-auto max-w-5xl space-y-10">
        {/* Page header */}
        <div className="space-y-1">
          <h1 className="text-2xl font-bold text-puntt-neutral-gray-12">
            Ask Puntt Canvas — Style Guide
          </h1>
          <p className="text-sm text-puntt-neutral-gray-10">
            All canvas block types and variants. Edit component files and reload
            to see changes instantly.
          </p>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* CanvasPane (full demo)                                            */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="CanvasPane — full demo">
          <div className="h-[520px] overflow-hidden rounded-lg border border-puntt-neutral-gray-5">
            {canvasDismissed ? (
              <div className="flex h-full items-center justify-center gap-3">
                <span className="text-sm text-puntt-neutral-gray-9">
                  Canvas dismissed.
                </span>
                <button
                  data-testid="style-guide-restore-canvas"
                  className="rounded-md bg-puntt-accent-9 px-3 py-1.5 text-xs font-medium text-base-white"
                  onClick={() => setCanvasDismissed(false)}
                >
                  Restore
                </button>
              </div>
            ) : (
              <CanvasPane
                canvas={FULL_CANVAS_DEMO}
                onDismiss={() => setCanvasDismissed(true)}
                skillName="Risk Review"
                onSectionClick={(id, title) =>
                  console.log("section clicked", id, title)
                }
              />
            )}
          </div>
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Headings                                                          */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Headings">
          <div className="space-y-2">
            <CanvasBlockRenderer
              block={{ type: "heading", level: 1, text: "Heading Level 1" }}
            />
            <CanvasBlockRenderer
              block={{ type: "heading", level: 2, text: "Heading Level 2" }}
            />
            <CanvasBlockRenderer
              block={{ type: "heading", level: 3, text: "Heading Level 3" }}
            />
          </div>
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Paragraph                                                         */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Paragraph">
          <CanvasBlockRenderer
            block={{
              type: "paragraph",
              text: "This is a sample paragraph block. It supports plain text content and is used for descriptive copy within canvas responses. Longer text wraps naturally within the available width.",
            }}
          />
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Divider                                                           */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Divider">
          <div className="space-y-2">
            <p className="text-sm text-puntt-neutral-gray-10">
              Above the divider
            </p>
            <CanvasBlockRenderer block={{ type: "divider" }} />
            <p className="text-sm text-puntt-neutral-gray-10">
              Below the divider
            </p>
          </div>
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Badges                                                            */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Badges — all colors × all sizes">
          {(["green", "amber", "red", "blue", "gray"] as const).map((color) => (
            <div key={color} className="mb-3 last:mb-0">
              <p className="mb-1.5 text-xs text-puntt-neutral-gray-8">
                {color}
              </p>
              <BlockRow>
                {(["sm", "md", "lg"] as const).map((size) => (
                  <BlockPreview key={size}>
                    <CanvasBlockRenderer
                      block={{
                        type: "badge",
                        label: `${color} / ${size}`,
                        color,
                        size,
                      }}
                    />
                  </BlockPreview>
                ))}
              </BlockRow>
            </div>
          ))}
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Callouts                                                          */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Callouts — all variants">
          <div className="space-y-3">
            {(["info", "warning", "success", "error"] as const).map(
              (variant) => (
                <CanvasBlockRenderer
                  key={variant}
                  block={{
                    type: "callout",
                    variant,
                    text: `This is a ${variant} callout. Use it to highlight important information for the user.`,
                  }}
                />
              ),
            )}
          </div>
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Metrics                                                           */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Metrics — all trend variants">
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-xs text-puntt-neutral-gray-8">
                Semantic variants (direction + outcome)
              </p>
              <BlockRow>
                {(
                  [
                    {
                      trend: "up-positive",
                      label: "Revenue",
                      value: "$1.2M",
                    },
                    {
                      trend: "up-negative",
                      label: "Error rate",
                      value: "4.7%",
                    },
                    {
                      trend: "down-positive",
                      label: "Bounce rate",
                      value: "12%",
                    },
                    {
                      trend: "down-negative",
                      label: "Conversions",
                      value: "3.2%",
                    },
                    {
                      trend: "neutral",
                      label: "Brand score",
                      value: "82%",
                    },
                  ] as const
                ).map(({ trend, label, value }) => (
                  <BlockPreview key={trend}>
                    <CanvasBlockRenderer
                      block={{ type: "metric", label, value, trend }}
                    />
                  </BlockPreview>
                ))}
                <BlockPreview key="no-trend">
                  <CanvasBlockRenderer
                    block={{
                      type: "metric",
                      label: "Total users",
                      value: "128",
                    }}
                  />
                </BlockPreview>
              </BlockRow>
            </div>
            <div>
              <p className="mb-2 text-xs text-puntt-neutral-gray-8">
                Legacy aliases (still supported; map to semantic variants)
              </p>
              <BlockRow>
                <BlockPreview>
                  <CanvasBlockRenderer
                    block={{
                      type: "metric",
                      label: "up → up-positive",
                      value: "↑ 24%",
                      trend: "up",
                    }}
                  />
                </BlockPreview>
                <BlockPreview>
                  <CanvasBlockRenderer
                    block={{
                      type: "metric",
                      label: "down → down-negative",
                      value: "↓ 8%",
                      trend: "down",
                    }}
                  />
                </BlockPreview>
              </BlockRow>
            </div>
          </div>
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Lists                                                             */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Lists — ordered and unordered">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="mb-2 text-xs text-puntt-neutral-gray-8">
                Unordered
              </p>
              <CanvasBlockRenderer
                block={{
                  type: "list",
                  ordered: false,
                  items: [
                    { type: "paragraph", text: "First item" },
                    { type: "paragraph", text: "Second item" },
                    { type: "paragraph", text: "Third item" },
                  ],
                }}
              />
            </div>
            <div>
              <p className="mb-2 text-xs text-puntt-neutral-gray-8">Ordered</p>
              <CanvasBlockRenderer
                block={{
                  type: "list",
                  ordered: true,
                  items: [
                    { type: "paragraph", text: "Step one" },
                    { type: "paragraph", text: "Step two" },
                    { type: "paragraph", text: "Step three" },
                  ],
                }}
              />
            </div>
          </div>
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Table                                                             */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Table — plain and with highlight rules">
          <div className="space-y-6">
            <div>
              <p className="mb-2 text-xs text-puntt-neutral-gray-8">Plain</p>
              <CanvasBlockRenderer
                block={{
                  type: "table",
                  headers: ["Name", "Role", "Department"],
                  rows: [
                    ["Alice", "Designer", "Brand"],
                    ["Bob", "Engineer", "Platform"],
                    ["Carol", "PM", "Growth"],
                  ],
                }}
              />
            </div>
            <div>
              <p className="mb-2 text-xs text-puntt-neutral-gray-8">
                With highlight rules
              </p>
              <CanvasBlockRenderer
                block={{
                  type: "table",
                  headers: ["Asset", "Status", "Risk"],
                  rows: [
                    ["Hero Banner", "Review", "High"],
                    ["Email Header", "Approved", "Low"],
                    ["Social Post", "Pending", "Medium"],
                  ],
                  highlightRules: {
                    High: "red",
                    Medium: "amber",
                    Low: "green",
                  },
                }}
              />
            </div>
          </div>
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Card                                                              */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Card — variants">
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-xs text-puntt-neutral-gray-8">
                Title only
              </p>
              <CanvasBlockRenderer
                block={{
                  type: "card",
                  title: "Simple Card",
                  children: [
                    { type: "paragraph", text: "Card body content goes here." },
                  ],
                }}
              />
            </div>
            <div>
              <p className="mb-2 text-xs text-puntt-neutral-gray-8">
                With subtitle + badge
              </p>
              <CanvasBlockRenderer
                block={{
                  type: "card",
                  title: "Brand Compliance",
                  subtitle: "Q3 2025 Review",
                  badge: {
                    type: "badge",
                    label: "High Risk",
                    color: "red",
                    size: "sm",
                  },
                  children: [
                    {
                      type: "paragraph",
                      text: "Three assets failed brand compliance checks.",
                    },
                    {
                      type: "metric",
                      label: "Failure Rate",
                      value: "6.4%",
                      trend: "up",
                    },
                  ],
                }}
              />
            </div>
          </div>
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Grid                                                              */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Grid — 2-col and 3-col">
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-xs text-puntt-neutral-gray-8">
                2 columns
              </p>
              <CanvasBlockRenderer
                block={{
                  type: "grid",
                  columns: 2,
                  children: [
                    {
                      type: "metric",
                      label: "Total Assets",
                      value: "128",
                      trend: "up",
                    },
                    {
                      type: "metric",
                      label: "Approved",
                      value: "104",
                      trend: "neutral",
                    },
                  ],
                }}
              />
            </div>
            <div>
              <p className="mb-2 text-xs text-puntt-neutral-gray-8">
                3 columns
              </p>
              <CanvasBlockRenderer
                block={{
                  type: "grid",
                  columns: 3,
                  children: [
                    {
                      type: "metric",
                      label: "High Risk",
                      value: "3",
                      trend: "down",
                    },
                    {
                      type: "metric",
                      label: "Medium Risk",
                      value: "11",
                      trend: "neutral",
                    },
                    {
                      type: "metric",
                      label: "Low Risk",
                      value: "24",
                      trend: "up",
                    },
                  ],
                }}
              />
            </div>
          </div>
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Expandable                                                        */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Expandable">
          <div className="space-y-3">
            <CanvasBlockRenderer
              block={{
                type: "expandable",
                title: "Without summary",
                children: [
                  { type: "paragraph", text: "Expanded content here." },
                ],
              }}
            />
            <CanvasBlockRenderer
              block={{
                type: "expandable",
                title: "With summary text",
                summary: "Click to see the full breakdown of brand issues.",
                children: [
                  {
                    type: "paragraph",
                    text: "Detailed explanation of each brand issue found during the review cycle.",
                  },
                  {
                    type: "list",
                    ordered: false,
                    items: [
                      {
                        type: "paragraph",
                        text: "Color mismatch on hero",
                      },
                      {
                        type: "paragraph",
                        text: "Missing legal disclaimer",
                      },
                    ],
                  },
                ],
              }}
            />
          </div>
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Section block                                                     */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Section block">
          <CanvasBlockRenderer
            block={{
              type: "section",
              id: "demo-section",
              title: "Clickable Section",
              children: [
                {
                  type: "paragraph",
                  text: "This section can be clicked to navigate or trigger actions. The onSectionClick handler logs to console.",
                },
              ],
            }}
            onSectionClick={(id, title) =>
              console.log("onSectionClick", { id, title })
            }
          />
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Source Link                                                       */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Source Link">
          <div className="space-y-2">
            <CanvasBlockRenderer
              block={{
                type: "source_link",
                title: "Brand Guidelines v2.4",
                url: "#",
                linkType: "document",
              }}
            />
            <CanvasBlockRenderer
              block={{
                type: "source_link",
                title: "Figma Design System",
                url: "#",
                linkType: "figma",
              }}
            />
            <CanvasBlockRenderer
              block={{
                type: "source_link",
                title: "External Reference",
                url: "#",
              }}
            />
          </div>
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* File Preview                                                      */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="File Preview — all file types">
          <div className="space-y-3">
            {(
              [
                {
                  fileType: "pdf",
                  fileName: "brand-guidelines.pdf",
                  pageCount: 12,
                },
                { fileType: "xlsx", fileName: "campaign-data.xlsx" },
                { fileType: "docx", fileName: "creative-brief.docx" },
                {
                  fileType: "pptx",
                  fileName: "q3-presentation.pptx",
                  pageCount: 24,
                },
              ] as const
            ).map((item) => (
              <CanvasBlockRenderer
                key={item.fileName}
                block={{ type: "file_preview", url: "#", ...item }}
              />
            ))}
          </div>
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Video Preview                                                     */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Video Preview">
          <CanvasBlockRenderer
            block={{
              type: "video_preview",
              url: "https://www.w3schools.com/html/mov_bbb.mp4",
              fileName: "sample-review.mp4",
            }}
          />
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Loading State                                                     */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="ProjectLoadingPane">
          <div className="h-64 overflow-hidden rounded-lg border border-puntt-neutral-gray-5">
            <ProjectLoadingPane />
          </div>
          <button
            data-testid="style-guide-toggle-loading-pane"
            className="mt-3 rounded-md bg-puntt-neutral-gray-3 px-3 py-1.5 text-xs font-medium text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-4"
            onClick={() => setShowLoadingPane((v) => !v)}
          >
            {showLoadingPane ? "Hide" : "Show"} full-height loading pane
          </button>
          {showLoadingPane && (
            <div className="mt-3 h-[480px] overflow-hidden rounded-lg border border-puntt-neutral-gray-5">
              <ProjectLoadingPane />
            </div>
          )}
        </StyleGuideSection>

        {/* ---------------------------------------------------------------- */}
        {/* Chart placeholder                                                 */}
        {/* ---------------------------------------------------------------- */}
        <StyleGuideSection title="Chart (placeholder — not yet implemented)">
          <div className="space-y-3">
            {(["bar", "pie", "line"] as const).map((chartType) => (
              <CanvasBlockRenderer
                key={chartType}
                block={{ type: "chart", chartType, data: {} }}
              />
            ))}
          </div>
        </StyleGuideSection>
      </div>
    </div>
  );
}
